import { IAuthManager } from "../../../../OW.Remoting/Remoting/Auth/IAuthManager";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { ILoginCredentialsProvider } from "./ILoginCredentialsProvider";
import { NavigationService } from "./NavigationService";
import { VerificationForm } from "./VerificationForm";

export class TotpForm extends VerificationForm {
    constructor(credentialsProvider: ILoginCredentialsProvider, authManager: IAuthManager, navigator: NavigationService, onSuccessfulLogin: () => any) {
        super(credentialsProvider, authManager, navigator, onSuccessfulLogin);

        this._initializeComponents();
    }

    //#region Methods
    protected override _getErrorElement(): HTMLElement {
        return document.getElementById("error-totp-div");
    }

    protected override _getSubmitLoginElement(): HTMLInputElement {
        return document.getElementById("send-totp-logon") as HTMLInputElement;
    }

    protected override _getChooseAnotherOptionLink(): HTMLElement {
        return document.getElementById("choose-another-option-totp-link");
    }

    protected override _getVerificationCode(): string {
        let code = "";
        const inputBoxes = TotpForm._getVerificationCodeInputBoxes();

        for (let i = 0; i < inputBoxes.length; i++) {
            code += (inputBoxes[i] as HTMLInputElement).value;
        }
        return code;
    }

    protected override _clearVerificationCodeInput(): void {
        const inputBoxes = TotpForm._getVerificationCodeInputBoxes();

        for (let i = 0; i < inputBoxes.length; i++) {
            (inputBoxes[i] as HTMLInputElement).value = "";
        }
    }

    protected override _focusVerificationCodeInput(): void {
        (TotpForm._getVerificationCodeInputBoxes()[0] as HTMLElement).focus();
    }

    private _initializeComponents(): void {
        this._loadTextLocalizations();
        this._registerTotpBoxesToSubmitByEnter();
        this._registerTotpBoxesListeners();
        this._registerClickEventListenerToElementById("back-to-login-form-from-totp-link", (_e) => this._backToLoginForm());
    }

    private static _getVerificationCodeInputBoxes(): HTMLCollectionOf<Element> {
        const container = TotpForm._getInputBoxesContainer();
        return container.getElementsByClassName("input-boxes-container__item");
    }

    private static _getInputBoxesContainer(): HTMLElement {
        return document.getElementById("totp-input-boxes-container");
    }

    private _loadTextLocalizations(): void {
        Localization.addLocData(this._getSubmitLoginElement(), "GUI-172655"); // Přihlásit
        Localization.addLocData(document.getElementById("totp-code-label"), "GUI-689095"); // Google Authenticator
        Localization.addLocData(document.getElementById("back-to-login-form-from-totp-link"), "GUI-689090"); // ZPĚT NA PŘIHLAŠOVACÍ ÚDAJE
        Localization.addLocData(document.getElementById("totp-form__header"), "GUI-689091"); // Dvoufázové ověření
    }

    private _registerTotpBoxesToSubmitByEnter(): void {
        const inputBoxes = TotpForm._getVerificationCodeInputBoxes();
        const submitElement = this._getSubmitLoginElement();

        for (let i = 0; i < inputBoxes.length; i++) {
            const inputBox = (inputBoxes[i] as HTMLInputElement);
            this._registerElementToSubmitByEnter(inputBox, submitElement);
        }
    }

    //#endregion Methods

    // #region Verification code input boxes implementation

    private _registerTotpBoxesListeners(): void {
        const container = TotpForm._getInputBoxesContainer();

        container.addEventListener("paste", (e) => {
            // Prevents the default implementation from copying to the currently active input.
            e.preventDefault();

            const clipboardText = e.clipboardData.getData("text/plain").trim();
            const inputBoxes = TotpForm._getVerificationCodeInputBoxes();

            for (let i = 0; i < inputBoxes.length; i++) {
                const inputBox = inputBoxes[i] as HTMLInputElement;
                if (clipboardText.length > i) {
                    inputBox.value = clipboardText[i];
                } else {
                    inputBox.value = "";
                }
            }
        });

        container.addEventListener("keydown", (e) => {
            let target = (e.srcElement || e.target) as HTMLInputElement;

            if (e.keyCode === 8 /* backspace */) {
                if (TotpForm._isEmpty(target)) {
                    target = TotpForm._activatePreviousInput(target);
                }
                TotpForm._deleteChar(target);
            }
            if (e.keyCode === 46 /* delete */) {
                if (TotpForm._isEmpty(target)) {
                    target = TotpForm._activateNextInput(target);
                }
                TotpForm._deleteChar(target);
            }
            if (e.keyCode === 37 /* left arrow */) {
                TotpForm._activatePreviousInput(target);
            }
            if (e.keyCode === 39 /* right arrow */) {
                TotpForm._activateNextInput(target);
            }
        });

        container.addEventListener("keypress", (e) => {
            e.preventDefault();

            const target = (e.srcElement || e.target) as HTMLInputElement;

            if (e.key >= "0" && e.key <= "9") {
                TotpForm._writeCharToInput(target, e.key);
                TotpForm._activateNextInput(target);
            }
        });
    }

    private static _writeCharToInput(target: HTMLInputElement, key: string): void {
        target.value = key.charAt(0);
    }

    private static _isEmpty(target: HTMLInputElement): boolean {
        return target.value === "";
    }

    private static _deleteChar(target: HTMLInputElement): void {
        target.value = "";
    }

    /**
     * Activates an element that is right after the currently active element.
     * @param target Currently active element.
     * @returns Returns the newly activated element or the given target element if no other element got activated.
     */
    private static _activateNextInput(target: HTMLInputElement): HTMLInputElement {
        let next = target;
        while ((next = (next.nextElementSibling as HTMLInputElement))) {
            if (next.tagName.toLowerCase() === "input") {
                next.focus();
                return next;
            }
        }
        return target;
    }

    /**
     * Activates an element that is just before the currently active element.
     * @param target Currently active element.
     * @returns Returns the newly activated element or the given target element if no other element got activated.
     */
    private static _activatePreviousInput(target: HTMLInputElement): HTMLInputElement {
        let previous = target;
        while ((previous = (previous.previousElementSibling as HTMLInputElement))) {
            if (previous.tagName.toLowerCase() === "input") {
                previous.focus();
                return previous;
            }
        }
        return target;
    }

    // #endregion
}
